import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { LexicalNode } from 'lexical';
import {
	$getSelection, $isRangeSelection, $isRootOrShadowRoot, $isNodeSelection,
	COMMAND_PRIORITY_CRITICAL, COMMAND_PRIORITY_EDITOR, createCommand, SELECTION_CHANGE_COMMAND
} from 'lexical';
import { isFunction } from "../../helpers/utils";
import { $isCustomHeadingNode } from "../nodes/customHeadingNode";
import { $isCustomLinkNode } from "../nodes/customLinkNode";
import { $isCustomParagraphNode } from "../nodes/customParagraphNode";
import { $isInlineImageNode } from "../nodes/InlineImageNode.tsx";
import { getSelectedNode } from '../utils/getSelectedNode';
import { $findMatchingParent} from '@lexical/utils';

const CSS_CLASS_CHANGE_COMMAND = createCommand('CSS_CLASS_CHANGE_COMMAND');

export default function ToolbarClassPlugin(): JSX.Element {
	const inputRef = useRef<HTMLInputElement>(null);
	const [editor] = useLexicalComposerContext();
	const [activeEditor, setActiveEditor] = useState(editor);
	const [classesEditable, setClassesEditable] = useState<boolean>(false);
	const [classes, setClasses] = useState<string>('');

	const $updateToolbar = useCallback(() => {
		const selection = $getSelection();

		if ($isRangeSelection(selection)) {
			const anchorNode = selection.anchor.getNode();
			let element =
				anchorNode.getKey() === 'root'
					? anchorNode
					: $findMatchingParent(anchorNode, (e) => {
						const parent = e.getParent();
						return parent !== null && $isRootOrShadowRoot(parent);
					});

			if (element === null) {
				element = anchorNode.getTopLevelElementOrThrow();
			}
			const elementKey = element.getKey();
			const elementDOM = activeEditor.getElementByKey(elementKey);

			const node = getSelectedNode(selection);
			const parent = node.getParent();

			//console.log(parent);
			//console.log(node);
			// console.log($isCustomHeadingNode(parent));
			// console.log(elementDOM);

			if (elementDOM !== null) {
				if ($isCustomLinkNode(parent) || $isCustomParagraphNode(parent) || $isCustomHeadingNode(parent)) {
					setClasses(parent.getClasses());
					setClassesEditable(true);
				} else {
					setClasses('');
					setClassesEditable(false);
				}
			}
		}

		if ($isNodeSelection(selection)) {
			const nodes = selection.getNodes();
			const node = nodes[0];
			const parent = node.getParent();

			if ($isInlineImageNode(node)) {
				if ($isCustomLinkNode(parent) || $isCustomParagraphNode(parent) || $isCustomHeadingNode(parent)) {
					setClasses(parent.getClasses());
					setClassesEditable(true);
				} else {
					setClasses('');
					setClassesEditable(false);
				}
			}
		}

	}, [activeEditor]);

	useEffect(() => {
		return editor.registerCommand(
			SELECTION_CHANGE_COMMAND,
			(_payload, newEditor) => {
				$updateToolbar();
				setActiveEditor(newEditor);
				return false;
			},
			COMMAND_PRIORITY_CRITICAL,
		);
	}, [editor, $updateToolbar]);

	useEffect(() => {
		return editor.registerCommand(
			CSS_CLASS_CHANGE_COMMAND,
			(payload: string) => {
				return changeClass(payload);
			},
			COMMAND_PRIORITY_EDITOR,
		);
	}, [editor]);

	useEffect(() => {
		activeEditor.registerUpdateListener(({editorState}) => {
			editorState.read(() => {
				$updateToolbar();
			});
		});
	}, [$updateToolbar, activeEditor, editor]);


	function changeClass(classes?: string): boolean {
		//console.log('changeClass', classes);
		const selection = $getSelection();

		if (selection) {
			let nodes: LexicalNode[] = selection.getNodes();

			if (nodes.length === 1) {
				let node: LexicalNode = nodes[0];

				while (node && !$isCustomParagraphNode(node) && !$isCustomLinkNode(node) && !$isCustomHeadingNode(node)) {
					node = node.getParent();
				}

				if (node && isFunction(node.setClasses)) {
					// console.log(node);
					// console.log(classes);

					node?.setClasses(classes);
					inputRef.current.focus();
					return true;
				}
			}
		}

		return false;
	}

	return (
		<div className="toolbar !pl-1.5">
			<input
				className="w-36"
				ref={inputRef}
				placeholder="Element css class"
				type="text"
				disabled={!classesEditable}
				value={classes}
			    onChange={(event) => {
					setClasses(event.target.value);
				}}
				onBlur={() => {
					editor.dispatchCommand(CSS_CLASS_CHANGE_COMMAND, classes);
				}}
				onKeyDown={(event) => {
					if (event.key == 'Enter') {
						event.preventDefault();
						editor.dispatchCommand(CSS_CLASS_CHANGE_COMMAND, classes);
					}
				}}
			/>
			{/*<Dump value={classesEditable}/>*/}
		</div>
	);
}
